import {FsdGroup, XUi} from './fsd-schema';

/**
 * Headings above the schema's own x-ui groups. A form passes a table to the settings form's
 * `sections` input; a section's member groups then render under its one h3 (no sub-headings),
 * wherever the first of them would have stood, as side-by-side columns in the order listed: each
 * member's first field on top and its other fields right under it. A first field that is an on/off
 * choice (a switch, or a select of "none" and one value) becomes a checkbox with a description, the
 * other fields its detail beside it, shown only while it is on. Generic: it applies to every template whose x-ui.group
 * names match, and a group in no section stays a standalone h3. Without a table (IFE) every group is
 * a standalone h3.
 *
 * Long term this belongs in the schema (an x-ui.section from vtkpy), so the UI names no group.
 */
export interface SectionHeading {
  title: string;
  groups: string[];
}

/** The VOD forms' table (user decision on #2761: Content Protection on VOD only). */
export const VOD_SECTIONS: SectionHeading[] = [
  {title: 'Content protection', groups: ['Watermark', 'DRM']},
];

/** What the form renders at top level: one group, or a section heading with its member groups. */
export interface FsdBlock {
  key: string;
  /** The SECTIONS heading; null for a standalone group. */
  title: string | null;
  groups: FsdGroup[];
  /** Section only: one column per member group, its first field leading (an on/off choice as a checkbox, see SchemaFormatSpecificComponent.toggles). */
  columns: FsdGroup[];
  /** A ruler before it: every block but the first. */
  ruled: boolean;
}

/**
 * The VOD forms' field hints: the DRMtoday Onboard availability window as days (user decision on
 * #2761). vtkpy hands onboard_window to DRMtoday as given; "available from" a day means from its
 * first second UTC, "available until" a day through its last second UTC (23:59:59Z, inclusive).
 * Long term these belong in the schema's own x-ui (vtkpy), like x-ui.section.
 */
export const VOD_FIELD_HINTS: { [path: string]: XUi } = {
  // The Watermark checkbox of Content protection: its label and the line beside it (the schema's own
  // description is a technical note, not user text), and the shorter names of its fields.
  'stardustmark.wm_id': {label: 'ID', flush: true},
  'stardustmark.fec': {wide: true},
  'encrypt': {label: 'Encryption', help: 'Applies encryption to the output.'},
  'onboard': {label: 'Enable DRMtoday Onboard', order: 2, wide: true},
  'onboard_tags': {order: 3, wide: true, help: 'Tags should be separated by commas (e.g., tag1, tag2, tag3).'},
  'onboard_window': {order: 4},
  'protection_systems': {
    order: 5,
    enum_help: {
      Widevine: 'Plays in Chrome, Firefox, Android and most smart TVs.',
      PlayReady: 'Plays in Edge, Windows, Xbox and many smart TVs.',
      FairPlay: 'Plays in Safari and on Apple devices: iPhone, iPad and Apple TV.',
    },
  },
  'watermark': {label: 'Watermark', help: 'Embeds an invisible forensic watermark into the video.'},
  'onboard_window.start_time': {day: 'start', help: 'Available from the first second of this day (UTC).'},
  'onboard_window.end_time': {day: 'end', help: 'Available through the last second of this day (UTC).'},
};
